import { useCV } from '../../hooks/useCV'
import { useTranslation } from '../../contexts/LanguageContext'
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '../ui/Accordion'
import { AutoSaveIndicator } from '../editor/AutoSaveIndicator'
import { CVCompletenessIndicator } from '../editor/CVCompletenessIndicator'
import { PhotoForm } from './PhotoForm'
import { PersonalInfoForm } from './PersonalInfoForm'
import { LinksForm } from './LinksForm'
import { SummaryForm } from './SummaryForm'
import { ExperienceForm } from './ExperienceForm'
import { EducationForm } from './EducationForm'
import { SkillsForm } from './SkillsForm'
import { LanguagesForm } from './LanguagesForm'
import { SettingsForm } from './SettingsForm'

const sectionIds = [
  { id: 'photo', key: 'photo' as const, icon: 'camera' },
  { id: 'personal', key: 'personalInfo' as const, icon: 'user' },
  { id: 'links', key: 'links' as const, icon: 'link' },
  { id: 'summary', key: 'summary' as const, icon: 'document' },
  { id: 'experience', key: 'experience' as const, icon: 'briefcase' },
  { id: 'education', key: 'education' as const, icon: 'academic' },
  { id: 'skills', key: 'skills' as const, icon: 'star' },
  { id: 'languages', key: 'languages' as const, icon: 'globe' },
  { id: 'settings', key: 'settings' as const, icon: 'cog' },
]

const icons: Record<string, JSX.Element> = {
  camera: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  link: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
    </svg>
  ),
  user: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  ),
  document: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  briefcase: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  academic: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path d="M12 14l9-5-9-5-9 5 9 5z" />
      <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
    </svg>
  ),
  star: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
    </svg>
  ),
  globe: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
    </svg>
  ),
  cog: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
}

export function CVFormSidebar() {
  const {
    cv,
    isDirty,
    lastSaved,
    updatePersonalInfo,
    updateSummary,
    addExperience,
    updateExperience,
    removeExperience,
    addEducation,
    updateEducation,
    removeEducation,
    updateSkills,
    addLanguage,
    updateLanguage,
    removeLanguage,
    reorderExperience,
    reorderEducation,
    updateSettings,
  } = useCV()
  const { t } = useTranslation()

  return (
    <div className="h-full min-h-0 flex flex-col">
      <div className="p-4 border-b border-slate-200 bg-white">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-900">{t.editor.title}</h2>
          <div className="flex items-center gap-3">
            <CVCompletenessIndicator cv={cv} />
            <AutoSaveIndicator isDirty={isDirty} lastSaved={lastSaved} />
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 pb-8">
        <Accordion defaultOpen={['personal']}>
          {sectionIds.map((section) => (
            <AccordionItem key={section.id} id={section.id}>
              {({ isOpen, toggle }) => (
                <>
                  <AccordionTrigger onClick={toggle} isOpen={isOpen}>
                    <span className="flex items-center gap-3">
                      <span className="text-slate-400">{icons[section.icon]}</span>
                      {t.sections[section.key]}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent isOpen={isOpen}>
                    {section.id === 'photo' && (
                      <PhotoForm
                        data={cv.personalInfo}
                        onChange={updatePersonalInfo}
                      />
                    )}
                    {section.id === 'personal' && (
                      <PersonalInfoForm
                        data={cv.personalInfo}
                        onChange={updatePersonalInfo}
                      />
                    )}
                    {section.id === 'links' && (
                      <LinksForm
                        data={cv.personalInfo}
                        onChange={updatePersonalInfo}
                      />
                    )}
                    {section.id === 'summary' && (
                      <SummaryForm
                        data={cv.summary}
                        onChange={updateSummary}
                        customLabel={cv.settings?.customLabels?.summary}
                        onCustomLabelChange={(v) => updateSettings({ customLabels: { ...cv.settings?.customLabels, summary: v } })}
                      />
                    )}
                    {section.id === 'experience' && (
                      <ExperienceForm
                        data={cv.experience}
                        onAdd={addExperience}
                        onUpdate={updateExperience}
                        onRemove={removeExperience}
                        onReorder={reorderExperience}
                        customLabel={cv.settings?.customLabels?.experience}
                        onCustomLabelChange={(v) => updateSettings({ customLabels: { ...cv.settings?.customLabels, experience: v } })}
                      />
                    )}
                    {section.id === 'education' && (
                      <EducationForm
                        data={cv.education}
                        onAdd={addEducation}
                        onUpdate={updateEducation}
                        onRemove={removeEducation}
                        onReorder={reorderEducation}
                        customLabel={cv.settings?.customLabels?.education}
                        onCustomLabelChange={(v) => updateSettings({ customLabels: { ...cv.settings?.customLabels, education: v } })}
                      />
                    )}
                    {section.id === 'skills' && (
                      <SkillsForm
                        data={cv.skills}
                        onChange={updateSkills}
                        customLabels={{
                          skills: cv.settings?.customLabels?.skills,
                          technical: cv.settings?.customLabels?.technical,
                          soft: cv.settings?.customLabels?.soft,
                          tools: cv.settings?.customLabels?.tools,
                        }}
                        onCustomLabelChange={(key, v) => updateSettings({ customLabels: { ...cv.settings?.customLabels, [key]: v } })}
                      />
                    )}
                    {section.id === 'languages' && (
                      <LanguagesForm
                        data={cv.languages}
                        onAdd={addLanguage}
                        onUpdate={updateLanguage}
                        onRemove={removeLanguage}
                        customLabel={cv.settings?.customLabels?.languages}
                        onCustomLabelChange={(v) => updateSettings({ customLabels: { ...cv.settings?.customLabels, languages: v } })}
                      />
                    )}
                    {section.id === 'settings' && (
                      <SettingsForm
                        data={cv.settings}
                        onChange={updateSettings}
                      />
                    )}
                  </AccordionContent>
                </>
              )}
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  )
}
